import { useMutation, useQueryClient } from '@tanstack/react-query';

import axios, { parseAxiosError } from '@/portainer/services/axios/axios';
import {
  mutationOptions,
  withError,
  withInvalidate,
} from '@/react-tools/react-query';
import {
  AutoUpdateResponse,
  GitAuthenticationResponse,
  RelativePathModel,
} from '@/react/portainer/gitops/types';
import { buildUrl } from '@/react/edge/edge-stacks/queries/buildUrl';
import {
  DeploymentType,
  EdgeStack,
  StaggerConfig,
} from '@/react/edge/edge-stacks/types';
import { EdgeGroup } from '@/react/edge/edge-groups/types';
import { Registry } from '@/react/portainer/registries/types/registry';

import { queryKeys } from '../../../queries/query-keys';
import { DeployerOptionsPayload } from '../../../components/DeployerOptions';

export interface UpdateEdgeStackGitPayload {
  id: EdgeStack['Id'];
  autoUpdate: AutoUpdateResponse | null;
  refName: string;
  authentication: GitAuthenticationResponse | null;
  groupIds: EdgeGroup['Id'][];
  deploymentType: DeploymentType;
  repullImageAndRedeploy: boolean;
  updateVersion: boolean;
  registries?: Array<Registry['Id']>;
  prePullImage: boolean;
  retryDeploy: boolean;
  retryPeriod: number;
  staggerConfig: StaggerConfig;
  deployerOptions: DeployerOptionsPayload;
  relativePath: RelativePathModel;
  // Helm fields - only ValuesFiles, Atomic, and Timeout can be updated
  // ChartPath and Version are read-only for Git deployments
  helmValuesFiles?: string[];
  helmAtomic?: boolean;
  helmTimeout?: string;
}

export function useUpdateEdgeStackGitMutation() {
  const queryClient = useQueryClient();

  return useMutation(
    updateEdgeStackGit,
    mutationOptions(
      withError('Failed updating stack'),
      withInvalidate(queryClient, [queryKeys.base()])
    )
  );
}

async function updateEdgeStackGit({
  id,
  helmValuesFiles,
  helmAtomic,
  helmTimeout,
  ...payload
}: UpdateEdgeStackGitPayload) {
  try {
    // Transform Helm field names to match backend expectations
    const apiPayload = {
      ...payload,
      // Backend expects field names without "Helm" prefix
      ...(helmValuesFiles && { ValuesFiles: helmValuesFiles }),
      ...(typeof helmAtomic !== 'undefined' && { Atomic: helmAtomic }),
      ...(typeof helmTimeout !== 'undefined' && { Timeout: helmTimeout }),
    };

    await axios.put(buildUrl(id, 'git'), apiPayload);
  } catch (err) {
    throw parseAxiosError(err as Error, 'Failed updating stack');
  }
}
